
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Canvas } from './components/Canvas';
import { Node, Connection, NodeType, Workflow } from './types';
import { NODE_CONFIGS } from './constants';
import { Icons } from './components/NodeComponents';
import { saveWorkflow, validateWorkflow, fetchWorkflows, upsertWorkflow, deleteWorkflow as deleteWorkflowApi } from './api';
import './styles.css';
import { useNavigate } from 'react-router-dom';
import logoofengage from './logoofengage.png';

const App = () => {
  const [nodes, setNodes] = useState<Node[]>([
    {
      id: 'node_start',
      type: 'start',
      position: { x: 100, y: 150 },
      data: {},
      isValid: true,
      expanded: false
    },
    {
      id: 'node_agent',
      type: 'agent_engine',
      position: { x: 500, y: 150 },
      data: { systemPrompt: 'You are a helpful assistant.', agentName: 'Customer_Support', status: 'Active' },
      isValid: true,
      expanded: true
    }
  ]);
  const [connections, setConnections] = useState<Connection[]>([
    { id: 'c1', source: 'node_start', target: 'node_agent', sourceType: 'start', targetType: 'agent_engine' }
  ]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isPaletteOpen, setIsPaletteOpen] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Saved workflows state
  const [savedWorkflows, setSavedWorkflows] = useState<Workflow[]>([]);
  const [isWorkflowsExpanded, setIsWorkflowsExpanded] = useState(true);
  const [currentWorkflowId, setCurrentWorkflowId] = useState<string | null>(null);
  const [workflowsLoaded, setWorkflowsLoaded] = useState(false);
  const [workflowLoadError, setWorkflowLoadError] = useState<string | null>(null);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  // Load workflows from backend
  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const workflows = await fetchWorkflows();
        if (isMounted) {
          setSavedWorkflows(workflows);
        }
      } catch (error) {
        console.error('Failed to load workflows:', error);
        if (isMounted) {
          setWorkflowLoadError('Failed to load workflows from the backend.');
        }
      } finally {
        if (isMounted) {
          setWorkflowsLoaded(true);
        }
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  // Load workflow from URL query parameter after workflows load
  useEffect(() => {
    if (!workflowsLoaded) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const workflowId = params.get('workflow');
    if (workflowId) {
      const workflow = savedWorkflows.find(w => w.id === workflowId);
      if (workflow) {
        setNodes(workflow.nodes);
        setConnections(workflow.connections);
        setCurrentWorkflowId(workflow.id);
        setSaveMessage({ type: 'success', text: `Loaded workflow: ${workflow.name}` });
      }
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [workflowsLoaded, savedWorkflows]);

  // Clear save message after a delay
  useEffect(() => {
    if (saveMessage) {
      const timer = setTimeout(() => setSaveMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [saveMessage]);

  const addNode = (type: NodeType) => {
    const id = `node_${Date.now()}`;
    const newNode: Node = {
      id,
      type,
      position: { x: 300 + (Math.random() * 50), y: 300 + (Math.random() * 50) },
      data: {
        agentName: type === 'agent_engine' ? 'New_Agent' : undefined,
        status: 'Active',
        memoryEnabled: type === 'memory' ? true : undefined,
        memoryType: type === 'memory' ? 'vertex_ai' : undefined,
        authType: type === 'llm' ? 'service_account' : undefined,
        modelProvider: type === 'llm' ? 'Google Vertex AI' : undefined,
        triggerPlatform: type === 'trigger' ? 'whatsapp' : undefined,
        whatsappVerifyToken: type === 'trigger' ? 'engaze_verify_token' : undefined,
      },
      isValid: true,
      expanded: (type === 'integration' || type === 'trigger') ? true : false // Auto expand complex nodes
    };
    setNodes([...nodes, newNode]);
  };

  // Handle save workflow
  const handleSaveWorkflow = useCallback(async (deploy: boolean = false) => {
    // Validate first
    const validation = validateWorkflow(nodes, connections);
    if (!validation.isValid) {
      setSaveMessage({ type: 'error', text: validation.errors.join('. ') });
      return;
    }

    // Get workflow name and description from agent node
    const agentNode = nodes.find(n => n.type === 'agent_engine');
    const workflowName = agentNode?.data.agentName || 'New_Agent';
    const workflowDescription = agentNode?.data.description || '';

    // Check if this workflow was already saved (has an identifier)
    const existingWorkflow = savedWorkflows.find(w => w.id === currentWorkflowId);
    const alreadySaved = existingWorkflow?.identifier;

    setIsSaving(true);
    try {
      let identifier = alreadySaved;

      // Always persist to backend so local saves also write artifacts.
      const result = await saveWorkflow(workflowName, nodes, connections);
      if (result.success) {
        identifier = result.identifier || identifier;
      } else {
        setSaveMessage({ type: 'error', text: result.error || 'Failed to save workflow' });
        setIsSaving(false);
        return;
      }

      const deployedFlag = deploy || existingWorkflow?.deployed || false;

      // Save to backend workflows list
      const workflowId = currentWorkflowId || `workflow_${Date.now()}`;
      const newWorkflow: Workflow = {
        id: workflowId,
        name: workflowName,
        description: workflowDescription,
        lastModified: Date.now(),
        nodes: nodes,
        connections: connections,
        deployed: deployedFlag,
        identifier: identifier,
      };

      const persisted = await upsertWorkflow(newWorkflow);
      setSavedWorkflows(prev => {
        const updated = prev.filter(w => w.id !== persisted.id);
        updated.push(persisted);
        return updated;
      });
      setCurrentWorkflowId(persisted.id);

      // If deploying, also save to deployed agents for main page
      if (deploy) {
        if (!identifier) {
          setSaveMessage({ type: 'error', text: `Agent "${workflowName}" is not saved yet. Please save the workflow first before deploying.` });
          setIsSaving(false);
          return;
        }
        setSaveMessage({ type: 'success', text: `Agent "${workflowName}" deployed successfully! It will appear in the main app.` });
      } else {
        setSaveMessage({ type: 'success', text: `Workflow "${workflowName}" saved! ID: ${identifier}` });
      }
    } catch (error: any) {
      setSaveMessage({ type: 'error', text: error.message || 'Failed to save workflow' });
    } finally {
      setIsSaving(false);
    }
  }, [nodes, connections, currentWorkflowId, savedWorkflows]);

  // Load a saved workflow
  const loadWorkflow = useCallback((workflow: Workflow) => {
    setNodes(workflow.nodes);
    setConnections(workflow.connections);
    setCurrentWorkflowId(workflow.id);
    setSaveMessage({ type: 'success', text: `Loaded workflow: ${workflow.name}` });
  }, []);

  // Delete a saved workflow
  const deleteWorkflow = useCallback(async (workflowId: string) => {
    try {
      await deleteWorkflowApi(workflowId);
      setSavedWorkflows(prev => prev.filter(w => w.id !== workflowId));
      if (currentWorkflowId === workflowId) {
        setCurrentWorkflowId(null);
      }
    } catch (error: any) {
      setSaveMessage({ type: 'error', text: error.message || 'Failed to delete workflow' });
    }
  }, [currentWorkflowId]);

  const handleImportWorkflow = useCallback(async (file: File) => {
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed?.nodes || !parsed?.connections) {
        setSaveMessage({ type: 'error', text: 'Invalid workflow JSON: missing nodes or connections.' });
        return;
      }
      const workflowId = parsed.id || `workflow_${Date.now()}`;
      const imported: Workflow = {
        id: workflowId,
        name: parsed.name || 'Imported Workflow',
        description: parsed.description || '',
        lastModified: Date.now(),
        nodes: parsed.nodes,
        connections: parsed.connections,
        deployed: false,
        identifier: parsed.identifier,
      };
      const persisted = await upsertWorkflow(imported);
      setNodes(persisted.nodes);
      setConnections(persisted.connections);
      setCurrentWorkflowId(persisted.id);
      setSaveMessage({ type: 'success', text: `Imported workflow: ${persisted.name}. Saved to backend.` });
      setSavedWorkflows(prev => {
        const updated = prev.filter(w => w.id !== persisted.id);
        updated.push(persisted);
        return updated;
      });
    } catch (error: any) {
      setSaveMessage({ type: 'error', text: error.message || 'Failed to import workflow.' });
    }
  }, []);

  // Download current workflow as JSON
  const handleDownloadWorkflow = useCallback(() => {
    const agentNode = nodes.find(n => n.type === 'agent_engine');
    const workflowName = agentNode?.data.agentName || 'Workflow';
    const workflowDescription = agentNode?.data.description || '';
    const currentWorkflow = savedWorkflows.find(w => w.id === currentWorkflowId);
    const workflowData = {
      id: currentWorkflowId || `workflow_${Date.now()}`,
      name: workflowName,
      description: workflowDescription,
      lastModified: Date.now(),
      nodes: nodes,
      connections: connections,
      deployed: currentWorkflow?.deployed || false,
      identifier: currentWorkflow?.identifier || undefined,
    };
    const blob = new Blob([JSON.stringify(workflowData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${workflowName.replace(/\s+/g, '_')}.workflow.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSaveMessage({ type: 'success', text: `Downloaded workflow: ${workflowName}` });
  }, [nodes, connections, currentWorkflowId, savedWorkflows]);

  // Create new workflow
  const createNewWorkflow = useCallback(() => {
    setNodes([
      {
        id: 'node_start',
        type: 'start',
        position: { x: 100, y: 150 },
        data: {},
        isValid: true,
        expanded: false
      },
      {
        id: 'node_agent',
        type: 'agent_engine',
        position: { x: 500, y: 150 },
        data: { systemPrompt: 'You are a helpful assistant.', agentName: 'New_Agent', status: 'Active' },
        isValid: true,
        expanded: true
      }
    ]);
    setConnections([
      { id: 'c1', source: 'node_start', target: 'node_agent', sourceType: 'start', targetType: 'agent_engine' }
    ]);
    setCurrentWorkflowId(null);
    setSelectedNodeId(null);
  }, []);

  // Topological Sort for Preview
  const getSortedNodes = () => {
    // 1. Build Adjacency List and In-Degree count
    const adj: Record<string, string[]> = {};
    const inDegree: Record<string, number> = {};
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    nodes.forEach(n => {
      adj[n.id] = [];
      inDegree[n.id] = 0;
    });

    connections.forEach(c => {
      if (adj[c.source]) adj[c.source].push(c.target);
      if (typeof inDegree[c.target] === 'number') inDegree[c.target]++;
    });

    // 2. Initialize Queue with 0 in-degree nodes (Roots)
    // Priority: Start nodes, then Top-Left nodes
    const queue: string[] = nodes
      .filter(n => inDegree[n.id] === 0)
      .sort((a, b) => {
        if (a.type === 'start') return -1;
        if (b.type === 'start') return 1;
        return (a.position.x + a.position.y) - (b.position.x + b.position.y);
      })
      .map(n => n.id);

    const sortedIds: string[] = [];

    // 3. Process Queue (BFS/Topological)
    while (queue.length > 0) {
      const u = queue.shift()!;
      sortedIds.push(u);

      if (adj[u]) {
        adj[u].forEach(v => {
          inDegree[v]--;
          if (inDegree[v] === 0) {
            queue.push(v);
          }
        });
      }
    }

    // 4. Handle Disconnected/Cycle nodes (append remaining)
    const visited = new Set(sortedIds);
    const remaining = nodes.filter(n => !visited.has(n.id)).sort((a, b) => a.position.x - b.position.x);

    return [...sortedIds.map(id => nodeMap.get(id)!), ...remaining];
  };

  const PreviewModal = () => {
    const sortedNodes = getSortedNodes();

    // Helper to find sources for a node
    const getSources = (targetId: string) => {
      return connections
        .filter(c => c.target === targetId)
        .map(c => nodes.find(n => n.id === c.source))
        .filter(Boolean) as Node[];
    };

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
        <div className="bg-white w-[600px] h-[80vh] rounded-xl shadow-2xl flex flex-col overflow-hidden animate-fade-in">
          <div className="h-14 border-b border-slate-100 flex items-center justify-between px-6 bg-white">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Workflow Execution Plan</h2>
            <button onClick={() => setShowPreview(false)} className="text-slate-400 hover:text-red-500">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
            <div className="relative">
              <div className="absolute left-6 top-4 bottom-4 w-0.5 bg-slate-200"></div>

              <div className="space-y-6">
                {sortedNodes.map((node, index) => {
                  const config = NODE_CONFIGS[node.type];
                  const sources = getSources(node.id);

                  return (
                    <div key={node.id} className="relative flex items-start gap-4">
                      <div className={`relative z-10 w-12 h-12 rounded-full border-4 border-slate-50 flex items-center justify-center text-sm font-bold text-white shadow-sm`} style={{ backgroundColor: config.accent }}>
                        {index + 1}
                      </div>

                      <div className="flex-1 bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-bold text-slate-800 text-sm">{node.type === 'agent_engine' && node.data.agentName ? node.data.agentName : config.title}</h3>
                          <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded text-slate-500 font-mono">{node.type}</span>
                        </div>

                        {sources.length > 0 && (
                          <div className="mb-2 flex flex-wrap gap-1">
                            <span className="text-[10px] text-slate-400 mr-1">Input from:</span>
                            {sources.map(s => (
                              <span key={s.id} className="text-[10px] bg-blue-50 text-blue-600 px-1.5 rounded border border-blue-100">
                                {NODE_CONFIGS[s.type].title}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="text-xs text-slate-600 space-y-1">
                          {node.type === 'agent_engine' && (
                            <p>System Prompt: <span className="font-mono text-slate-500 truncate block">{node.data.systemPrompt?.substring(0, 50)}...</span></p>
                          )}
                          {node.type === 'llm' && (
                            <p>Model: <span className="font-medium">{node.data.modelProvider || 'N/A'}</span></p>
                          )}
                          {node.type === 'integration' && node.data.selectedToolAgent && (
                            <p>Selected Agent: <span className="font-medium">{node.data.selectedToolAgent}</span></p>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="relative flex items-center gap-4 mt-6">
                <div className="w-12 h-12 flex items-center justify-center relative z-10">
                  <div className="w-4 h-4 bg-slate-300 rounded-full ring-4 ring-slate-100"></div>
                </div>
                <div className="text-xs text-slate-400 font-medium uppercase tracking-widest">End of Workflow</div>
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-slate-100 bg-white flex justify-end gap-3">
            <button onClick={() => setShowPreview(false)} className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-50 rounded">Edit</button>
            <button
              onClick={async () => {
                await handleSaveWorkflow(true); // Deploy = true
                setShowPreview(false);
              }}
              disabled={isSaving}
              className="px-6 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded shadow-sm disabled:opacity-50"
            >
              {isSaving ? 'Deploying...' : 'Deploy Agent'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen font-sans">
      <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 z-20 shadow-sm relative">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsPaletteOpen(!isPaletteOpen)}
            className="p-2 hover:bg-slate-100 rounded text-slate-500"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-1 -ml-1 text-slate-400 hover:text-slate-600 rounded-lg transition-colors flex items-center justify-center mr-2"
              title="Back to Admin Suite"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            </button>
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center shadow-md overflow-hidden">
              <img src={logoofengage} alt="Engage Logo" className="w-6 h-6 object-contain" />
            </div>
            <span className="font-bold text-gray-800 tracking-tight">Agent Builder</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={handleDownloadWorkflow} className="flex items-center gap-2 px-4 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" /></svg>
            Download
          </button>
          <button onClick={() => setShowPreview(true)} className="flex items-center gap-2 px-4 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            Preview Flow
          </button>
          <button
            onClick={() => handleSaveWorkflow(false)}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-black rounded-lg text-xs font-bold text-white shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                Saving...
              </>
            ) : (
              'Save Workflow'
            )}
          </button>
        </div>
      </header>

      {/* Save Message Toast */}
      {saveMessage && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg transition-all animate-fade-in ${saveMessage.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
          <div className="flex items-center gap-2 text-sm font-medium">
            {saveMessage.type === 'success' ? '✅' : '❌'} {saveMessage.text}
          </div>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        <aside
          className={`bg-white border-r border-gray-200 flex flex-col z-10 shadow-[4px_0_24px_-12px_rgba(0,0,0,0.1)] transition-all duration-300 ease-in-out ${isPaletteOpen ? 'w-64 opacity-100 translate-x-0' : 'w-0 opacity-0 -translate-x-full overflow-hidden'}`}
        >
          <div className="p-4 min-w-[16rem] flex-1 overflow-y-auto">
            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4 pl-1">Node Palette</h3>
            <div className="space-y-2">
              {(Object.keys(NODE_CONFIGS) as NodeType[]).map(type => {
                const config = NODE_CONFIGS[type];
                const Icon = Icons[config.icon as keyof typeof Icons];
                return (
                  <div
                    key={type}
                    onClick={() => addNode(type)}
                    className="p-3 rounded-lg border border-slate-100 bg-white hover:border-blue-400 hover:shadow-md cursor-pointer transition-all flex items-center gap-3 group relative overflow-hidden active:scale-[0.98]"
                  >
                    <div className={`absolute left-0 top-0 bottom-0 w-1`} style={{ backgroundColor: config.accent }}></div>

                    <div className="w-8 h-8 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: `${config.accent}15` }}>
                      <div style={{ color: config.accent }}>
                        <Icon />
                      </div>
                    </div>

                    <div className="ml-0">
                      <div className="text-xs font-bold text-slate-700 group-hover:text-blue-700 transition-colors">{config.title}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Workflows Section */}
            <div className="mt-6 border-t border-slate-200 pt-4">
              <div
                className="flex items-center justify-between cursor-pointer mb-3"
                onClick={() => setIsWorkflowsExpanded(!isWorkflowsExpanded)}
              >
                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Workflows</h3>
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform ${isWorkflowsExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {isWorkflowsExpanded && (
                <div className="space-y-2">
                  {/* New Workflow Button */}
                  <div
                    onClick={createNewWorkflow}
                    className="p-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-all flex items-center gap-3 group"
                  >
                    <div className="w-8 h-8 rounded flex items-center justify-center shrink-0 bg-blue-100">
                      <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <div className="text-xs font-bold text-slate-600 group-hover:text-blue-700">New Workflow</div>
                  </div>

                  {/* Import Workflow Button */}
                  <div
                    onClick={() => importInputRef.current?.click()}
                    className="p-3 rounded-lg border border-dashed border-slate-300 bg-white hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-all flex items-center gap-3 group"
                  >
                    <div className="w-8 h-8 rounded flex items-center justify-center shrink-0 bg-slate-100">
                      <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12v8m0-8l-4 4m4-4l4 4M8 8h8a2 2 0 002-2V4H6v2a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="text-xs font-bold text-slate-600 group-hover:text-blue-700">Import Workflow</div>
                  </div>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept="application/json"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleImportWorkflow(file);
                      }
                      if (e.target) {
                        e.target.value = '';
                      }
                    }}
                  />

                  {workflowLoadError && (
                    <div className="text-[10px] text-red-500 text-center">
                      {workflowLoadError}
                    </div>
                  )}

                  {/* Saved Workflows List */}
                  {savedWorkflows.length === 0 ? (
                    <div className="text-xs text-slate-400 text-center py-3">
                      No saved workflows yet
                    </div>
                  ) : (
                    savedWorkflows.map(workflow => (
                      <div
                        key={workflow.id}
                        className={`p-3 rounded-lg border bg-white cursor-pointer transition-all flex items-center gap-3 group relative overflow-hidden ${currentWorkflowId === workflow.id
                          ? 'border-blue-400 shadow-md'
                          : 'border-slate-100 hover:border-slate-300 hover:shadow-sm'
                          }`}
                      >
                        <div className={`absolute left-0 top-0 bottom-0 w-1 ${workflow.deployed ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>

                        <div
                          className="flex-1 min-w-0"
                          onClick={() => loadWorkflow(workflow)}
                        >
                          <div className="text-xs font-bold text-slate-700 truncate">{workflow.name}</div>
                          <div className="flex items-center gap-2 mt-1">
                            {workflow.deployed && (
                              <span className="text-[9px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium">
                                Deployed
                              </span>
                            )}
                            <span className="text-[9px] text-slate-400">
                              {new Date(workflow.lastModified).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        {/* Delete button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete workflow "${workflow.name}"?`)) {
                              deleteWorkflow(workflow.id);
                            }
                          }}
                          className="p-1 rounded hover:bg-red-100 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete workflow"
                        >
                          <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="p-4 border-t border-slate-100 bg-slate-50 min-w-[16rem]">
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <span className="font-bold">Pro Tip:</span> Space + Drag to pan
            </div>
          </div>
        </aside>

        <main className="flex-1 relative bg-slate-50/50">
          <Canvas
            nodes={nodes}
            connections={connections}
            setNodes={setNodes}
            setConnections={setConnections}
            onNodeSelect={setSelectedNodeId}
            selectedNodeId={selectedNodeId}
          />
        </main>
      </div>

      {showPreview && <PreviewModal />}
    </div>
  );
};

export default App;
